import React, { useState } from "react";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_BASE_URL || "http://localhost:3051";

interface ConfigPanelProps {
  config: {
    enabled: boolean;
    timezone: string;
    logRetentionDays: number;
  };
  password: string;
  onConfigUpdate: () => void;
}

const ConfigPanel: React.FC<ConfigPanelProps> = ({
  config,
  password,
  onConfigUpdate,
}) => {
  const [enabled, setEnabled] = useState(config.enabled);
  const [logRetentionDays, setLogRetentionDays] = useState(
    config.logRetentionDays
  );
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${API_BASE_URL}/api/dominant-monitoring/config`,
        {
          enabled,
          logRetentionDays,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "x-admin-password": password,
          },
        }
      );

      if (response.data.success) {
        setMessage({
          type: "success",
          text: "Configuration saved successfully",
        });
        onConfigUpdate();
      }
    } catch (err: any) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Failed to save configuration",
      });
    } finally {
      setSaving(false);
    }
  };

  const hasChanges =
    enabled !== config.enabled || logRetentionDays !== config.logRetentionDays;

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-4">
        ⚙️ Global Configuration
      </h2>

      <div className="space-y-6">
        {/* Master Enable/Disable */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              Monitoring Status
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Enable or disable all monitoring and logging
            </p>
          </div>
          <button
            onClick={() => setEnabled(!enabled)}
            className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
              enabled ? "bg-green-600" : "bg-gray-300"
            }`}
          >
            <span
              className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                enabled ? "translate-x-7" : "translate-x-1"
              }`}
            />
          </button>
        </div>

        {/* Timezone Display */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Timezone
          </label>
          <div className="px-4 py-2 bg-gray-50 rounded-md text-gray-700">
            {config.timezone} (Fixed)
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Timezone is hardcoded to Asia/Jerusalem in the backend
          </p>
        </div>

        {/* Log Retention */}
        <div>
          <label
            htmlFor="retention"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Log Retention Period (days)
          </label>
          <input
            type="number"
            id="retention"
            min="1"
            max="365"
            value={logRetentionDays}
            onChange={(e) =>
              setLogRetentionDays(parseInt(e.target.value) || 90)
            }
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="text-xs text-gray-500 mt-1">
            Logs older than this will be automatically deleted
          </p>
        </div>

        {/* Message */}
        {message && (
          <div
            className={`p-3 rounded-md ${
              message.type === "success"
                ? "bg-green-50 border border-green-200"
                : "bg-red-50 border border-red-200"
            }`}
          >
            <p
              className={`text-sm ${
                message.type === "success" ? "text-green-800" : "text-red-800"
              }`}
            >
              {message.text}
            </p>
          </div>
        )}

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className="px-6 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? "Saving..." : "Save Configuration"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfigPanel;
